import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import { setBrochure } from '@store/actionSlices/brochure'
import {
  BucketSource,
  type RootStateTypeExtra,
  type SessionMap,
} from '@store/types'

import Container from '@components/container'
import DataHandler from '@components/data-handler'
import FadeContainer from '@components/fade-container'
import IdleTimeHandler, {
  useIdleTimeHandler,
} from '@components/idle-time-handler'

import {
  BrochureData,
  GalleryData,
  selectFromResult,
  useGetBrochureQuery,
} from '@api/brochure'

import {
  CarouselProvider,
  Tab,
} from '@adUtilities/components/carousel-provider'
import { CarouselControlType } from '@adUtilities/constants/carousel'
import { FileType } from '@adUtilities/constants/common'

import BrochureSkeleton from './brochure-skeleton'

export interface BrochureProps {
  session: SessionMap | undefined
  projectName: string
  brochure: Array<BrochureData>
  appIdleControls: {
    duration: number
    active: boolean
  }
  effectType: string
  autoPlayIntervalInSeconds: number
}

function Brochure({
  session,
  projectName,
  brochure,
  appIdleControls,
  effectType,
  autoPlayIntervalInSeconds,
}: BrochureProps) {
  const dispatch = useDispatch()

  const [isSessionConnected, setIsConnected] = React.useState(false)
  const [toggleControls, setToggleControls] = React.useState(true)
  const [activeTabIndex, setActiveTabIndex] = React.useState(0)
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0)

  const isAppIdle = useIdleTimeHandler({
    isConnected: isSessionConnected,
    timeoutInSeconds: appIdleControls?.duration || 3,
    isIdleActive: appIdleControls?.active || false,
  })

  const brochurePayload = useGetBrochureQuery(
    { projectName },
    { selectFromResult }
  )

  const slides = React.useMemo(
    () =>
      brochure.length > 0
        ? brochure[activeTabIndex].Images.map((item: GalleryData) => ({
            id: item.id,
            label: item.imageName,
            src: item.imageSource,
            thumbnail: item.imageSource,
            bucketType: BucketSource.Legacy,
            fileType: FileType.Image,
            noSpliceUrl: false,
          }))
        : [],
    [brochure, activeTabIndex]
  )

  const handleCarouselProviderClick = () => setToggleControls((state) => !state)

  React.useEffect(() => {
    const { brochureData } = brochurePayload
    if (brochure.length === 0 && brochureData.length > 0) {
      dispatch(setBrochure(brochureData))
    }
  }, [brochurePayload, brochure])

  React.useEffect(() => {
    if (session) {
      const {
        connected,
        brochure: {
          galleryControlV2: {
            activeTabIndex: activeTabIndexFirebase,
            activeSlideIndex: activeSlideIndexFirebase,
          },
        },
      } = session

      setIsConnected(connected)
      setActiveTabIndex(activeTabIndexFirebase)
      setActiveSlideIndex(activeSlideIndexFirebase)
    }
  }, [session])

  return (
    <Container>
      <DataHandler
        payloads={{
          ...brochurePayload,
          data: brochure,
        }}
        skeletonFrame={<BrochureSkeleton />}
      >
        {!isSessionConnected && (
          <IdleTimeHandler className="absolute top-8 z-2">
            <FadeContainer show={toggleControls}>
              <Tab
                items={brochure.map((item) => ({ text: item.label }))}
                handleOnItemClick={setActiveTabIndex}
                activeItemIndex={activeTabIndex}
                toggleControls={!toggleControls}
              />
            </FadeContainer>
          </IdleTimeHandler>
        )}

        <TransitionGroup className="h-full">
          <CSSTransition
            key={activeTabIndex}
            classNames="transition-fade"
            timeout={500}
          >
            <CarouselProvider
              gallerySettings={{
                effectType,
                autoPlayIntervalInSeconds,
              }}
              slides={slides}
              activeSlideIndex={activeSlideIndex}
              onContainerClick={handleCarouselProviderClick}
              showControl={!isSessionConnected}
              controlType={CarouselControlType.Thumbnail}
              mousewheel={!isSessionConnected}
              isAppIdle={isAppIdle}
              enableKeyboardControl={!toggleControls && !isSessionConnected}
            ></CarouselProvider>
          </CSSTransition>
        </TransitionGroup>
      </DataHandler>
    </Container>
  )
}

const mapStateToProps = ({
  firestore: { session },
  projectIdentity: { projectName },
  brochure,
  projectConfig: {
    propertyGalleryTab,
    gallerySettings: { type, intervalInSeconds },
  },
}: RootStateTypeExtra) => ({
  session,
  projectName,
  brochure,
  appIdleControls: propertyGalleryTab,
  effectType: type,
  autoPlayIntervalInSeconds: intervalInSeconds,
})

export default connect(mapStateToProps)(Brochure)
